import React, { useEffect, useRef, useState, useContext } from "react";
import classNames from "classnames/bind";
import styles from "./CustomerCare.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faCheckCircle,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { ToastContext } from "~/components/context/ToastContext";
import { ModalContext } from "~/components/context/ModalContext";
import { BASE_CUSTOMER_CARE_URL } from "~/helper/apiService";
import { exportPhonesToExcelClient, generateExcelFileName } from "./customerCareHelpers";

const cx = classNames.bind(styles);

const CARE_LIST_FILTERS_STORAGE_KEY = "customerCare.careList.filters.v1";

function CareListPage() {
  const [loading, setLoading] = useState(false);
  const [careList, setCareList] = useState([]);
  const [dbHasData, setDbHasData] = useState(null); // null=unknown, boolean=known
  const [showSyncButton, setShowSyncButton] = useState(false); // allow manual show when DB already has data
  const [minDays, setMinDays] = useState("30");
  const [minVisits, setMinVisits] = useState("2");
  const [maxVisits, setMaxVisits] = useState(""); // Lọc khoảng Tổng lượt đi (đến)
  const [limit, setLimit] = useState("100");
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPhones, setSelectedPhones] = useState([]);
  const [careNote, setCareNote] = useState("");
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);
  const didLoadFiltersRef = useRef(false);

  // Load filter config from localStorage on first mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(CARE_LIST_FILTERS_STORAGE_KEY);
      if (!raw) {
        didLoadFiltersRef.current = true;
        return;
      }
      const parsed = JSON.parse(raw);

      const toStr = (v) => (v === null || v === undefined ? undefined : String(v));

      const nextMinDays = toStr(parsed?.minDays);
      const nextMinVisits = toStr(parsed?.minVisits);
      const nextMaxVisits = toStr(parsed?.maxVisits);
      const nextLimit = toStr(parsed?.limit);

      if (nextMinDays !== undefined) setMinDays(nextMinDays);
      if (nextMinVisits !== undefined) setMinVisits(nextMinVisits);
      if (nextMaxVisits !== undefined) setMaxVisits(nextMaxVisits);
      if (nextLimit !== undefined) setLimit(nextLimit);
    } catch (_) {
      // ignore storage parse errors
    } finally {
      didLoadFiltersRef.current = true;
    }
  }, []);

  // Check whether CSKH DB already has data (to hide Sync button and avoid accidental clicks)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${BASE_CUSTOMER_CARE_URL}/db-status`, {
          credentials: "include",
        });
        const data = await response.json();
        if (cancelled) return;
        if (response.ok && data?.success) {
          const hasData = Boolean(data.hasData);
          setDbHasData(hasData);
          // Default behavior:
          // - If DB has NO data => show sync button (needed)
          // - If DB already has data => hide sync button to prevent misclick, but allow user to toggle it on
          setShowSyncButton(!hasData);
        } else {
          // If status check fails, default to "has data" to be safe (hide sync)
          setDbHasData(true);
          setShowSyncButton(false);
        }
      } catch (_) {
        if (!cancelled) {
          setDbHasData(true); // safe default: hide sync
          setShowSyncButton(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist filter config to localStorage when changed (after initial load)
  useEffect(() => {
    if (!didLoadFiltersRef.current) return;
    try {
      localStorage.setItem(
        CARE_LIST_FILTERS_STORAGE_KEY,
        JSON.stringify({ minDays, minVisits, maxVisits, limit })
      );
    } catch (_) {
      // ignore storage errors (private mode/quota)
    }
  }, [minDays, minVisits, maxVisits, limit]);

  const fetchCareList = async () => {
    setLoading(true);
    try {
      const minDaysValue = minDays === "" ? 30 : parseInt(minDays) || 30;
      const minVisitsValue = minVisits === "" ? null : parseInt(minVisits) || 0;
      const maxVisitsValue = maxVisits === "" ? null : parseInt(maxVisits) || 0;
      const limitValue = limit === "" ? 100 : parseInt(limit) || 100;
      const url =
        `${BASE_CUSTOMER_CARE_URL}/care-list?minDays=${minDaysValue}` +
        `${minVisitsValue !== null ? `&minVisits=${minVisitsValue}` : ""}` +
        `${maxVisitsValue !== null ? `&maxVisits=${maxVisitsValue}` : ""}` +
        `${limitValue ? `&limit=${limitValue}` : ""}`;
      
      console.log("[FETCH-CARE-LIST] Bắt đầu tìm kiếm");
      console.log("[FETCH-CARE-LIST] URL:", url);
      console.log("[FETCH-CARE-LIST] Tham số:", {
        minDays: minDaysValue,
        minVisits: minVisitsValue,
        maxVisits: maxVisitsValue,
        limit: limitValue
      });
      
      const response = await fetch(url, {
        credentials: "include",
      });
      
      console.log("[FETCH-CARE-LIST] Response status:", response.status);
      console.log("[FETCH-CARE-LIST] Response ok:", response.ok);
      
      const data = await response.json();
      
      console.log("[FETCH-CARE-LIST] Response data:", {
        success: data.success,
        careListLength: data.careList ? data.careList.length : 0,
        total: data.total,
        error: data.error
      });
      
      if (data.success) {
        setCareList(data.careList || []);
        setTotalCount(data.total || 0);
        console.log("[FETCH-CARE-LIST] Đã set careList với", data.careList ? data.careList.length : 0, "phần tử");
        console.log("[FETCH-CARE-LIST] Total count:", data.total || 0);
        if (data.careList && data.careList.length > 0) {
          console.log("[FETCH-CARE-LIST] 5 số điện thoại đầu tiên:", data.careList.slice(0, 5).map(c => c.phone));
        }
      } else {
        console.error("[FETCH-CARE-LIST] Lỗi từ server:", data.error);
        showToast(data.error || "Lỗi khi lấy danh sách", "error");
      }
    } catch (error) {
      console.error("[FETCH-CARE-LIST] Lỗi kết nối:", {
        message: error.message,
        stack: error.stack,
        error: error
      });
      showToast("Lỗi kết nối: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_CUSTOMER_CARE_URL}/refresh`, {
        credentials: "include",
      });
      const data = await response.json();
      if (data.success) {
        showToast("Đã refresh dữ liệu thành công", "success");
        fetchCareList();
      } else {
        showToast(data.error || "Lỗi khi refresh", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const getCareListPhones = () =>
    [...new Set((careList || []).map((c) => c.phone).filter(Boolean))];

  const handleSelectAll = () => {
    const allPhones = getCareListPhones();
    setSelectedPhones((prev) => {
      const prevSet = new Set(prev);
      const allSelected =
        allPhones.length > 0 && allPhones.every((p) => prevSet.has(p));
      return allSelected ? [] : allPhones;
    });
  };

  const handleSelectPhone = (phone) => {
    setSelectedPhones((prev) => {
      if (prev.includes(phone)) return prev.filter((p) => p !== phone);
      return [...prev, phone];
    });
  };

  const handleMarkAsCared = () => {
    if (selectedPhones.length === 0) {
      showToast("Vui lòng chọn ít nhất một khách hàng", "error");
      return;
    }

    showModal({
      title: "Xác nhận đánh dấu Đã CSKH",
      message: `Bạn có chắc chắn muốn đánh dấu ${selectedPhones.length} khách hàng là Đã CSKH?`,
      onConfirm: async () => {
        try {
          const response = await fetch(`${BASE_CUSTOMER_CARE_URL}/mark-as-cared`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              phones: selectedPhones,
              note: careNote,
            }),
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          if (data.success) {
            showToast(`Đã đánh dấu ${data.updated} khách hàng là Đã CSKH`, "success");
            const savedNote = careNote;
            // Lưu lại selectedPhones trước khi clear để export
            const phonesForExport = [...selectedPhones];
            setSelectedPhones([]);
            setCareNote("");
            // Tự động tải file Excel sau khi chăm sóc thành công (EXPORT Ở CLIENT theo số đã tick)
            try {
              console.log("[EXPORT-EXCEL-CLIENT] Export theo selectedPhones đã tick. Số dòng:", phonesForExport.length);
              if (phonesForExport.length === 0) {
                showToast("Không có dữ liệu để export", "error");
              } else {
                const result = await exportPhonesToExcelClient(
                  phonesForExport,
                  generateExcelFileName(savedNote)
                );
                console.log("[EXPORT-EXCEL-CLIENT] Export OK:", result);
              }
            } catch (excelError) {
              console.error("[EXPORT-EXCEL-CLIENT] Lỗi export:", excelError);
              showToast("Lỗi khi export Excel: " + excelError.message, "error");
            }
            handleRefresh().catch(err => console.error("Error refreshing:", err));
            fetchCareList().catch(err => console.error("Error fetching care list:", err));
          } else {
            showToast(data.error || "Lỗi khi đánh dấu", "error");
          }
        } catch (error) {
          console.error("Error marking as cared:", error);
          showToast("Lỗi kết nối: " + error.message, "error");
        }
        return true;
      },
      onCancel: null,
    });
  };

  const handleMarkAsCaredQuick = () => {
    if (selectedPhones.length === 0) {
      showToast("Vui lòng chọn ít nhất một khách hàng", "error");
      return;
    }

    showModal({
      title: "Xác nhận đánh dấu Đã CSKH",
      message: `Bạn có chắc chắn muốn đánh dấu ${selectedPhones.length} khách hàng là Đã CSKH?`,
      onConfirm: async () => {
        try {
          const response = await fetch(`${BASE_CUSTOMER_CARE_URL}/mark-as-cared`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              phones: selectedPhones,
              note: "",
            }),
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          if (data.success) {
            showToast(`Đã đánh dấu ${data.updated} khách hàng là Đã CSKH`, "success");
            // Lưu lại selectedPhones trước khi clear để export
            const phonesForExport = [...selectedPhones];
            setSelectedPhones([]);
            // Tự động tải file Excel sau khi chăm sóc thành công (EXPORT Ở CLIENT theo số đã tick)
            try {
              console.log("[EXPORT-EXCEL-CLIENT] Export (quick) theo selectedPhones đã tick. Số dòng:", phonesForExport.length);
              if (phonesForExport.length === 0) {
                showToast("Không có dữ liệu để export", "error");
              } else {
                const result = await exportPhonesToExcelClient(
                  phonesForExport,
                  generateExcelFileName("")
                );
                console.log("[EXPORT-EXCEL-CLIENT] Export OK:", result);
              }
            } catch (excelError) {
              console.error("[EXPORT-EXCEL-CLIENT] Lỗi export:", excelError);
              showToast("Lỗi khi export Excel: " + excelError.message, "error");
            }
            handleRefresh().catch(err => console.error("Error refreshing:", err));
            fetchCareList().catch(err => console.error("Error fetching care list:", err));
          } else {
            showToast(data.error || "Lỗi khi đánh dấu", "error");
          }
        } catch (error) {
          console.error("Error marking as cared:", error);
          showToast("Lỗi kết nối: " + error.message, "error");
        }
        return true;
      },
      onCancel: null,
    });
  };

  const getSeverityClass = (days) => {
    if (days >= 90) return "severity-lost";
    if (days >= 60) return "severity-cold";
    if (days >= 30) return "severity-risk";
    return "severity-urgent";
  };

  const renderTable = () => {
    if (careList.length === 0) {
      return <div className={cx("no-data")}>Không có dữ liệu</div>;
    }

    return (
      <>
        <table className={cx("table")}>
          <thead>
            <tr>
              <th className={cx("th", "checkbox-col")}>
                <input
                  type="checkbox"
                  checked={
                    (() => {
                      const allPhones = [...new Set(careList.map((c) => c.phone).filter(Boolean))];
                      return allPhones.length > 0 && allPhones.every((p) => selectedPhones.includes(p));
                    })()
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th className={cx("th")}>SĐT</th>
              <th className={cx("th")}>Tổng lượt đi</th>
              <th className={cx("th")}>Ngày đi gần nhất</th>
              <th className={cx("th")}>Số ngày chưa quay lại</th>
              <th className={cx("th")}>Mức độ</th>
              <th className={cx("th")}>Trạng thái CSKH</th>
            </tr>
          </thead>
          <tbody>
            {careList.map((customer, index) => (
              <tr key={index} className={cx("tr")}>
                <td className={cx("td", "checkbox-col")}>
                  <input
                    type="checkbox"
                    checked={selectedPhones.includes(customer.phone)}
                    onChange={() => handleSelectPhone(customer.phone)}
                  />
                </td>
                <td className={cx("td")}>{customer.phone}</td>
                <td className={cx("td")}>{customer.totalVisits}</td>
                <td className={cx("td")}>{customer.lastVisitDate}</td>
                <td className={cx("td")}>{customer.daysSinceLastVisit !== null ? customer.daysSinceLastVisit : "-"}</td>
                <td className={cx("td", "severity")}>
                  <span className={cx("severity-badge", getSeverityClass(customer.daysSinceLastVisit))}>
                    {customer.severityEmoji} {customer.severity}
                  </span>
                </td>
                <td className={cx("td")}>{customer.careStatus}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className={cx("mobile-card-container")}>
          {careList.map((customer, index) => (
            <div key={index} className={cx("mobile-card")}>
              <div className={cx("mobile-card-header")}>
                <div className={cx("mobile-card-title")}>{customer.phone}</div>
                <input
                  type="checkbox"
                  className={cx("mobile-card-checkbox")}
                  checked={selectedPhones.includes(customer.phone)}
                  onChange={() => handleSelectPhone(customer.phone)}
                />
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Tổng lượt đi:</span>
                <span className={cx("mobile-card-value")}>{customer.totalVisits}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Ngày đi gần nhất:</span>
                <span className={cx("mobile-card-value")}>{customer.lastVisitDate}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Số ngày chưa quay lại:</span>
                <span className={cx("mobile-card-value")}>{customer.daysSinceLastVisit !== null ? customer.daysSinceLastVisit : "-"}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Mức độ:</span>
                <span className={cx("mobile-card-value")}>
                  <span className={cx("severity-badge", getSeverityClass(customer.daysSinceLastVisit))}>
                    {customer.severityEmoji} {customer.severity}
                  </span>
                </span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Trạng thái CSKH:</span>
                <span className={cx("mobile-card-value")}>{customer.careStatus}</span>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className={cx("customer-care")}>
      <div className={cx("header")}>
        <h2 className={cx("title")}>Danh Sách Cần Chăm Sóc</h2>
        <div className={cx("actions")}>
          {showSyncButton && (
            <button
              className={cx("button", "sync-button")}
              title="Đồng bộ lượt đi từ Google Sheets sang CSDL. Không xoá lịch sử CSKH; trạng thái CSKH chỉ cập nhật an toàn."
              onClick={() => {
                showModal({
                  title: "Xác nhận đồng bộ CSDL",
                  message:
                    "Đồng bộ sẽ cập nhật lại dữ liệu lượt đi (danh sách cần chăm sóc) từ Google Sheets sang CSDL. " +
                    "Lịch sử CSKH trong CSDL không bị xoá. Tiếp tục?",
                  onConfirm: async () => {
                    setLoading(true);
                    try {
                      const response = await fetch(`${BASE_CUSTOMER_CARE_URL}/sync-to-db`, {
                        method: "POST",
                        credentials: "include",
                      });
                      const data = await response.json();
                      if (data.success) {
                        showToast(data.message || "Đã đồng bộ dữ liệu sang CSDL", "success");
                        setDbHasData(true); // hide sync after first successful sync
                        setShowSyncButton(false);
                        // Sau khi sync, tự động load danh sách từ CSDL
                        fetchCareList();
                      } else {
                        showToast(data.error || "Lỗi khi đồng bộ dữ liệu", "error");
                      }
                    } catch (error) {
                      showToast("Lỗi kết nối: " + error.message, "error");
                    } finally {
                      setLoading(false);
                    }
                    return true;
                  },
                  onCancel: null,
                });
              }}
              disabled={loading}
            >
              Đồng bộ CSDL
            </button>
          )}

          {dbHasData === true && (
            <button
              type="button"
              className={cx("button")}
              style={{ opacity: 0.9 }}
              onClick={() => setShowSyncButton((v) => !v)}
              disabled={loading}
              aria-label={showSyncButton ? "Ẩn nút đồng bộ" : "Hiện nút đồng bộ"}
              title={
                showSyncButton
                  ? "Đang hiện nút đồng bộ (bấm để ẩn)"
                  : "Đang ẩn nút đồng bộ (bấm để hiện)"
              }
            >
              {showSyncButton ? <FaEyeSlash aria-hidden="true" /> : <FaEye aria-hidden="true" />}
            </button>
          )}
          <button
            className={cx("button", "refresh-button")}
            onClick={handleRefresh}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} /> Refresh
          </button>
        </div>
      </div>

      <div className={cx("tab-content")}>
        <div className={cx("filters")}>
          <div className={cx("filter-group")}>
            <label>Số ngày chưa quay lại ≥</label>
            <input
              type="text"
              value={minDays}
              onChange={(e) => setMinDays(e.target.value)}
              className={cx("filter-input")}
            />
          </div>
          <div className={cx("filter-group")}>
            <label>Tổng lượt đi (từ - đến)</label>
            <input
              type="text"
              value={minVisits}
              onChange={(e) => setMinVisits(e.target.value)}
              className={cx("filter-input")}
              placeholder="Từ"
            />
            <input
              type="text"
              value={maxVisits}
              onChange={(e) => setMaxVisits(e.target.value)}
              className={cx("filter-input")}
              placeholder="Đến"
              style={{ marginTop: 8 }}
            />
          </div>
          <div className={cx("filter-group")}>
            <label>Số lượng hiển thị</label>
            <input
              type="text"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className={cx("filter-input")}
            />
          </div>
          <button
            className={cx("button", "search-button")}
            onClick={fetchCareList}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSearch} spin={loading} /> Bắt đầu tìm kiếm
          </button>
          {selectedPhones.length > 0 && (
            <button
              className={cx("button", "cared-button")}
              onClick={handleMarkAsCaredQuick}
            >
              <FontAwesomeIcon icon={faCheckCircle} /> Đã chăm sóc ({selectedPhones.length})
            </button>
          )}
        </div>
        {totalCount > 0 && (
          <div className={cx("count-info")}>
            Hiển thị {careList.length} / {totalCount} khách hàng
          </div>
        )}

        {selectedPhones.length > 0 && (
          <div className={cx("mark-as-cared-section")}>
            <div className={cx("selected-info")}>
              Đã chọn: {selectedPhones.length} khách hàng
            </div>
            <div className={cx("note-input-group")}>
              <label>Ghi chú (tùy chọn):</label>
              <input
                type="text"
                value={careNote}
                onChange={(e) => setCareNote(e.target.value)}
                placeholder="Nhập ghi chú..."
                className={cx("note-input")}
              />
            </div>
            <button
              className={cx("button", "mark-button")}
              onClick={handleMarkAsCared}
            >
              <FontAwesomeIcon icon={faCheckCircle} /> Đánh dấu Đã CSKH ({selectedPhones.length})
            </button>
          </div>
        )}

        <div className={cx("table-container")}>
          {renderTable()}
        </div>
      </div>
    </div>
  );
}

export default CareListPage;
